/**
 * Minimal SwapAnalytics client (LMWFY pattern).
 * Fire-and-forget POST to https://analytics.swapp1990.org/api/events.
 * Never blocks render; never throws into the UI.
 */

const ENDPOINT = 'https://analytics.swapp1990.org/api/events';
const DEVICE_KEY = 'swap_analytics_device_id';
const TIMEOUT_MS = 5000;

let appId: string | null = null;

function uuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function getDeviceId(): string {
  try {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) {
      id = uuid();
      localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
  } catch {
    return uuid();
  }
}

function detectEnvironment(): string {
  if (typeof window === 'undefined') return 'production';
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local')) {
    return 'development';
  }
  return 'production';
}

/** Capture UTM params from the current URL for social attribution. */
export function getUtmMetadata(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  const out: Record<string, string> = {};
  for (const key of ['utm_source', 'utm_campaign', 'utm_medium', 'utm_content', 'utm_term'] as const) {
    const value = params.get(key);
    if (value) out[key] = value;
  }
  return out;
}

export function initAnalytics(id: string): void {
  appId = id;
}

export function track(
  eventName: string,
  metadata: Record<string, unknown> = {},
): void {
  if (!appId) return;

  const payload = {
    appId,
    eventName,
    environment: detectEnvironment(),
    metadata,
    deviceId: getDeviceId(),
  };

  void sendWithRetry(payload, 1);
}

async function sendWithRetry(
  payload: {
    appId: string;
    eventName: string;
    environment: string;
    metadata: Record<string, unknown>;
    deviceId: string;
  },
  retriesLeft: number,
): Promise<void> {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    try {
      await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
        keepalive: true,
      });
    } finally {
      clearTimeout(timer);
    }
  } catch {
    if (retriesLeft > 0) {
      void sendWithRetry(payload, retriesLeft - 1);
    }
    // swallow — analytics must never crash the page
  }
}
